import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { suppliersAPI, coreAPI, purchasingAPI, fetchAllPages, SupplierProfile, SupplierDashboardStats, Site, CommodityGroup, CommodityType } from '../../lib/api';
import api from '../../lib/api';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useView } from '../../contexts/ViewContext';
import { SupplierWithStatus, MapLocation } from './types';
import { mapStatusToType, cardOtifGrade } from './utils';
import type { SupplierFilters } from './SearchFilterBar';
import { EMPTY_FILTERS } from './SearchFilterBar';
import { useOTIFBands, toDeliveryBands } from '../../hooks/useOTIFBands';
import { type DeliveryThresholds } from '../../components/dashboard/SiteKPIComponents';

export function useSuppliers() {
  const navigate = useNavigate();
  const { viewKey, getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const [suppliers, setSuppliers] = useState<SupplierWithStatus[]>([]);
  const [stats, setStats] = useState<SupplierDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  // CR-3953: Filter state for supplier list
  const [filters, setFilters] = useState<SupplierFilters>(EMPTY_FILTERS);
  const [siteOptions, setSiteOptions] = useState<{ value: string; label: string }[]>([]);
  // CR-6269: the viewer's OTIF Grade Thresholds — what the OTIF quick-filter
  // chips are named after and what a supplier's rate is banded on.
  const { bands: otifBands } = useOTIFBands();
  const deliveryBands: DeliveryThresholds = useMemo(() => toDeliveryBands(otifBands), [otifBands]);

  // Commodity filter data
  const [commodityGroups, setCommodityGroups] = useState<CommodityGroup[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<CommodityType[]>([]);
  const [commoditySupplierMap, setCommoditySupplierMap] = useState<Record<string, { groups: number[]; types: number[] }>>({});

  // Current tab (all, sites, groups)
  const [currentTab, setCurrentTab] = useState<'all' | 'sites' | 'groups'>('sites');
  // CR-4578: When user clicks a Supplier Group card, filter sites tab to that group
  const [selectedGroupName, setSelectedGroupName] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Search
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Supplier locations for map
  const [mapLocations, setMapLocations] = useState<MapLocation[]>([]);

  // Build commodity dropdown options
  const commodityGroupOptions = useMemo(() =>
    commodityGroups
      .filter((g) => g.enabled)
      .map((g) => ({ value: String(g.id), label: g.name })),
    [commodityGroups]
  );

  const commodityTypeOptions = useMemo(() => {
    let types = commodityTypes.filter((t) => t.enabled);
    // If a commodity group is selected, only show types for that group
    if (filters.commodity_group) {
      types = types.filter((t) => String(t.commodity_group) === filters.commodity_group);
    }
    return types.map((t) => ({ value: String(t.id), label: t.name }));
  }, [commodityTypes, filters.commodity_group]);

  // A load walks several pages, so a site switch can start a new one before
  // the old one lands; only the newest load may write state.
  const fetchSeq = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      setError(null);
      setLoading(true);

      const viewParams = getViewQueryParams();
      const [supplierPages, statsResponse, sitesResponse, allContracts, groupsResponse, typesResponse, supplierMapResponse] = await Promise.all([
        // CR-5556: Removed is_approved: true to allow Approval Gate Status filter to work
        // V9: also return suppliers whose relationship with this company has
        // been deactivated — hidden below unless "Include inactive" is on, the
        // same way company-inactive suppliers already are.
        // CR-8208: card_metrics asks for the OTIF tile and the tile mode (the
        // other light-list callers do not pay for them).
        // Walk pages — the backend caps page_size at 1000, and search, chips,
        // counts, groups and the map all work on the loaded list, so a customer
        // with more suppliers than that never saw the rest.
        // A newer load (site switch) stops this walk instead of fetching the
        // rest of a list nobody will see; the rejection is ignored below.
        fetchAllPages<SupplierProfile>(
          (page) => (seq !== fetchSeq.current
            ? Promise.reject(new Error('Superseded by a newer supplier load'))
            : suppliersAPI.getProfiles({ page_size: 1000, page, minimal: true, include_inactive: 'yes', card_metrics: 'true', ...viewParams })),
        ),
        suppliersAPI.getDashboardStats(viewParams),
        coreAPI.getAccessibleSites(),
        // Walk pages — the backend clamps page_size to 100, so the
        // "has active contract" set was built from one page of contracts.
        fetchAllPages<Record<string, unknown>>(
          (page) => api.get('/purchasing/contract-prices/', { params: { page_size: 100, page } }),
        ).catch(() => [] as Record<string, unknown>[]),
        purchasingAPI.getCommodityGroups({ enabled: true }).catch(() => ({ data: [] as CommodityGroup[] })),
        purchasingAPI.getCommodityTypes({ enabled: true }).catch(() => ({ data: [] as CommodityType[] })),
        purchasingAPI.getCommoditySupplierMap().catch(() => ({ data: {} })),
      ]);
      if (seq !== fetchSeq.current) return;

      // Each page is cached on its own, so one fetched later can repeat a row.
      const suppliersData = Array.from(new Map(supplierPages.map((s) => [s.id, s])).values());

      // CR-3953: Build site filter options
      const sites = (sitesResponse.data as unknown as Site[]) || [];
      setSiteOptions(
        sites.map((s) => ({ value: String(s.id), label: s.name }))
      );

      // Commodity data
      const groupsData = Array.isArray(groupsResponse.data) ? groupsResponse.data : (groupsResponse.data as unknown as { results: CommodityGroup[] }).results || [];
      const typesData = Array.isArray(typesResponse.data) ? typesResponse.data : (typesResponse.data as unknown as { results: CommodityType[] }).results || [];
      setCommodityGroups(groupsData);
      setCommodityTypes(typesData);
      setCommoditySupplierMap(supplierMapResponse.data as Record<string, { groups: number[]; types: number[] }>);

      // Build set of supplier names with active contracts
      const contractSupplierNames = new Set(
        allContracts
          .filter((c) => c['status'] === 'active')
          .map((c) => c['supplier_name'] as string)
          .filter(Boolean)
      );

      // Map suppliers with real onboarding_status from backend
      const suppliersWithStatus: SupplierWithStatus[] = suppliersData.map((s: SupplierProfile) => ({
        ...s,
        onboarding_status_derived: mapStatusToType(s.onboarding_status, s.is_approved),
        has_contract: contractSupplierNames.has(s.company_name),
        draft_progress: (s as unknown as Record<string, unknown>)['draft_progress'] as number ?? 0,
        is_supplier_group: false,
        // CR-3825: Use real API value for questionnaire responses
        has_questionnaire_responses: !!s.has_questionnaire_responses,
      }));

      // CR-4556: Build supplier group entries for the "Supplier Groups" tab
      // CR-4578: Capture group_id so the card click can navigate to /suppliers/groups/:id
      // CR-6682: derived from the supplier profiles this page already shows,
      // for every user. It used to come from a separate /core/companies/ list
      // that only Helix could call, which left the tab permanently empty for a
      // customer admin — including for a group they had just created here. That
      // list was also unscoped, so a Helix user saw groups that the rest of the
      // page (and the group's own detail view) excluded under the active view
      // context. Reading the profiles keeps the tab, the supplier list and the
      // group detail all agreeing on one scope.
      const groupSources: Array<Record<string, unknown>> = suppliersData
        .map((s: SupplierProfile) => (s as unknown as Record<string, unknown>)['company_details'] as Record<string, unknown> | undefined)
        .filter((details): details is Record<string, unknown> => !!details);
      const groupMap = new Map<number, { id: number; name: string; count: number }>();
      for (const comp of groupSources) {
        const groupId = comp['group'] as number | undefined;
        const groupName = comp['group_name'] as string | undefined;
        if (groupId && groupName) {
          const existing = groupMap.get(groupId);
          if (existing) {
            existing.count++;
          } else {
            groupMap.set(groupId, { id: groupId, name: groupName, count: 1 });
          }
        }
      }
      // Create synthetic SupplierWithStatus entries for each group
      const groupEntries: SupplierWithStatus[] = Array.from(groupMap.values()).map((g, idx) => ({
        id: -(idx + 1),  // Negative IDs to avoid collision
        company: -(idx + 1),
        company_name: g.name,
        onboarding_status: 'approved',
        is_approved: true,
        is_active: true,
        has_questionnaire_responses: false,
        onboarding_status_derived: 'approved' as const,
        has_contract: false,
        draft_progress: 0,
        is_supplier_group: true,
        supplier_count: g.count,
        group_id: g.id,
      } as unknown as SupplierWithStatus));

      suppliersWithStatus.push(...groupEntries);

      setSuppliers(suppliersWithStatus);
      setStats(statsResponse.data);

      // Extract locations for map — use real coordinates, city lookup, or country fallback
      // CR-4563 rework: City-level lookup for accurate pin placement
      const cityCoords: Record<string, [number, number]> = {
        'london': [51.51, -0.13], 'birmingham': [52.48, -1.90], 'manchester': [53.48, -2.24],
        'leeds': [53.80, -1.55], 'sheffield': [53.38, -1.47], 'liverpool': [53.41, -2.98],
        'bristol': [51.45, -2.59], 'newcastle': [54.98, -1.61], 'nottingham': [52.95, -1.15],
        'coventry': [52.41, -1.51], 'leicester': [52.63, -1.13], 'derby': [52.92, -1.47],
        'warrington': [53.39, -2.59], 'wolverhampton': [52.59, -2.13], 'stoke': [53.00, -2.18],
        'reading': [51.45, -0.97], 'southampton': [50.91, -1.40], 'portsmouth': [50.80, -1.09],
        'plymouth': [50.37, -4.14], 'exeter': [50.72, -3.53], 'swindon': [51.56, -1.78],
        'gloucester': [51.86, -2.24], 'oxford': [51.75, -1.26], 'cambridge': [52.21, 0.12],
        'norwich': [52.63, 1.30], 'ipswich': [52.06, 1.16], 'peterborough': [52.57, -0.24],
        'milton keynes': [52.04, -0.76], 'northampton': [52.24, -0.90], 'luton': [51.88, -0.42],
        'aberdeen': [57.15, -2.09], 'edinburgh': [55.95, -3.19], 'glasgow': [55.86, -4.25],
        'cardiff': [51.48, -3.18], 'swansea': [51.62, -3.94], 'belfast': [54.60, -5.93],
        'york': [53.96, -1.08], 'hull': [53.74, -0.33], 'chester': [53.19, -2.89],
        'bath': [51.38, -2.36], 'cheltenham': [51.90, -2.07], 'worcester': [52.19, -2.22],
        'dudley': [52.51, -2.08], 'walsall': [52.58, -1.98], 'bolton': [53.58, -2.43],
        'stockport': [53.41, -2.16], 'preston': [53.76, -2.70], 'bradford': [53.79, -1.75],
        'huddersfield': [53.65, -1.78], 'wakefield': [53.68, -1.50], 'doncaster': [53.52, -1.13],
        'rotherham': [53.43, -1.36], 'telford': [52.68, -2.45], 'crewe': [53.10, -2.44],
        'stafford': [52.81, -2.12], 'berlin': [52.52, 13.41], 'munich': [48.14, 11.58],
        'hamburg': [53.55, 9.99], 'frankfurt': [50.11, 8.68], 'paris': [48.86, 2.35],
        'lyon': [45.76, 4.84], 'amsterdam': [52.37, 4.90], 'dublin': [53.35, -6.26],
        'toronto': [43.65, -79.38], 'new york': [40.71, -74.01], 'chicago': [41.88, -87.63],
      };
      const countryCoords: Record<string, [number, number]> = {
        'United Kingdom': [52.5, -1.5], 'United States': [39.8, -98.5], 'Germany': [51.2, 10.4],
        'France': [46.6, 2.2], 'Poland': [52.0, 19.4], 'Italy': [42.5, 12.5], 'Spain': [40.4, -3.7],
        'Netherlands': [52.1, 5.3], 'Belgium': [50.8, 4.4], 'Sweden': [62.0, 15.0],
        'Norway': [60.5, 8.5], 'Denmark': [56.0, 10.0], 'Switzerland': [46.8, 8.2],
        'Austria': [47.5, 14.6], 'Czech Republic': [49.8, 15.5], 'Japan': [36.2, 138.3],
        'China': [35.9, 104.2], 'India': [20.6, 79.0], 'Canada': [56.1, -106.3],
        'Australia': [-25.3, 133.8], 'Ireland': [53.4, -8.2], 'Portugal': [39.4, -8.2],
        'Finland': [61.9, 25.7], 'Romania': [45.9, 24.97], 'Hungary': [47.2, 19.5],
        'Turkey': [38.9, 35.2], 'Mexico': [23.6, -102.6], 'Brazil': [-14.2, -51.9],
        'South Korea': [35.9, 127.8], 'Singapore': [1.35, 103.8], 'Malaysia': [4.2, 101.9],
        'Thailand': [15.9, 100.5], 'UK': [52.5, -1.5], 'USA': [39.8, -98.5],
      };
      const locations = suppliersWithStatus
        .map((s: SupplierWithStatus) => {
          const details = s.company_details as unknown as Record<string, unknown>;
          // CR-4563 rework: Use company_id as fallback, avoid undefined
          const supplierId = s.company_id || s.company;
          if (!supplierId) return null;
          const lat = details?.['latitude'] ? parseFloat(String(details['latitude'])) : null;
          const lng = details?.['longitude'] ? parseFloat(String(details['longitude'])) : null;
          if (lat && lng) return { id: supplierId, name: s.company_name, lat, lng };
          // CR-4563 rework: Try city-level lookup for accurate placement
          const city = String(details?.['city'] || '').toLowerCase().trim();
          if (city && cityCoords[city]) {
            const [cLat, cLng] = cityCoords[city];
            const offset = ((supplierId % 10) - 5) / 500;
            return { id: supplierId, name: s.company_name, lat: cLat + offset, lng: cLng + offset };
          }
          // Fall back to country-level with offset
          const country = String(details?.['country'] || '');
          const coords = countryCoords[country];
          if (coords) {
            const offset = ((supplierId % 20) - 10) / 20;
            const offset2 = (((supplierId * 7) % 20) - 10) / 20;
            return { id: supplierId, name: s.company_name, lat: coords[0] + offset, lng: coords[1] + offset2 };
          }
          return null;
        })
        .filter((loc): loc is MapLocation => loc !== null);
      setMapLocations(locations);

    } catch (err) {
      if (seq === fetchSeq.current) setError(getErrorMessage(err, 'Failed to load suppliers'));
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
    // viewKey is not read here — it is the refetch trigger when the top-bar
    // view switcher changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey, getViewQueryParams]);

  useEffect(() => {
    if (viewContextLoading) return;
    fetchData();
  }, [fetchData, viewContextLoading, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  // CR-8208: the OTIF grade of a supplier card — its OTIF over the card's
  // Last 12 months, resolved on the configured OTIF bands with the dashboard's
  // rule (highest band whose minimum the rate reaches). A supplier with no
  // judged delivery (N/A) has no grade: it is counted only under "All".
  const otifBandOf = (s: SupplierWithStatus): string => cardOtifGrade(s.otif_rate, deliveryBands);

  // CR-8208: Delivery Metric Mode is one customer setting, carried on every row.
  const deliveryMetricMode = suppliers.find((s) => s.delivery_metric_mode)?.delivery_metric_mode ?? 'both';

  // Reusable matcher so quick-filter chips can show counts without re-applying themselves.
  const matchesFilters = (s: SupplierWithStatus, opts: { skipRisk?: boolean; skipQuestionnaire?: boolean; skipOtif?: boolean } = {}) => {
    if (searchQuery && !s.company_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (currentTab === 'groups' && !s.is_supplier_group) return false;
    if (currentTab === 'sites' && s.is_supplier_group) return false;
    if (selectedGroupName && !s.is_supplier_group) {
      const companyGroupName = (s.company_details as unknown as Record<string, unknown>)?.['group_name'] as string | undefined;
      if (companyGroupName !== selectedGroupName) return false;
    }
    // CR-5556: Configurable Approval Gate Status filter (replaces hardcoded 'approved' filter)
    const supplierStatus = s.onboarding_status_derived || 'draft';
    if (filters.approval_gate_status) {
      if (filters.approval_gate_status === 'pending_initial') {
        // "Pending Approval" matches both pending_initial and due_diligence statuses
        if (supplierStatus !== 'pending_initial' && supplierStatus !== 'due_diligence') return false;
      } else if (supplierStatus !== filters.approval_gate_status) {
        return false;
      }
    }
    if (filters.site) {
      const customerSites = s.customers?.toLowerCase() || '';
      const siteLabel = siteOptions.find((o) => o.value === filters.site)?.label?.toLowerCase() || '';
      if (!customerSites.includes(siteLabel)) return false;
    }
    // Deactivated suppliers are hidden by default — they are usually retired
    // duplicates. The "Include inactive" filter reveals them: an inactive
    // supplier still owns its POs and its code is dropped from the ERP feed
    // filter, so it has to be findable to diagnose or reverse.
    if (filters.include_inactive !== 'yes' && s.company_details?.is_active === false) return false;
    // V9: a relationship this company switched off is inactive *for this
    // company* even though the supplier is live elsewhere.
    if (filters.include_inactive !== 'yes' && s.relationship_is_active === false) return false;
    if (filters.supplier_code) {
      const code = s.company_details?.supplier_code?.toLowerCase() || '';
      if (!code.includes(filters.supplier_code.toLowerCase())) return false;
    }
    // CR-9077: the Strategic Supplier toggle (per customer), not is_preferred.
    if (filters.strategic === 'yes' && !s.is_strategic) return false;
    if (filters.strategic === 'no' && s.is_strategic) return false;
    if (!opts.skipRisk && filters.risk && (s.risk_level || 'none') !== filters.risk) return false;
    if (!opts.skipQuestionnaire && filters.questionnaire) {
      const qOverall = s.questionnaire_status?.overall || 'none';
      if (qOverall !== filters.questionnaire) return false;
    }
    // OTIF band is an approved-supplier delivery metric — supplier groups and
    // non-approved suppliers (no delivery history) are excluded when active.
    if (!opts.skipOtif && filters.otif_band) {
      if (s.is_supplier_group) return false;
      if (otifBandOf(s) !== filters.otif_band) return false;
    }
    if (filters.commodity_group) {
      const supplierCommodities = commoditySupplierMap[String(s.company_id ?? s.company)];
      if (!supplierCommodities || !supplierCommodities.groups.includes(Number(filters.commodity_group))) return false;
    }
    if (filters.commodity_type) {
      const supplierCommodities = commoditySupplierMap[String(s.company_id ?? s.company)];
      if (!supplierCommodities || !supplierCommodities.types.includes(Number(filters.commodity_type))) return false;
    }
    return true;
  };

  const filteredSuppliers = suppliers.filter((s) => matchesFilters(s));

  // Counts for risk chips — apply all filters except the risk one itself.
  const riskCounts: Record<string, number> = { '': 0, low: 0, medium: 0, high: 0, critical: 0 };
  for (const s of suppliers) {
    if (matchesFilters(s, { skipRisk: true })) {
      riskCounts[''] += 1;
      const key = (s.risk_level || 'none');
      if (key in riskCounts) riskCounts[key] += 1;
    }
  }
  // Counts for questionnaire chips — apply all filters except the questionnaire one.
  const questionnaireCounts: Record<string, number> = { '': 0, up_to_date: 0, expiring: 0, overdue: 0, pending: 0, none: 0 };
  for (const s of suppliers) {
    if (matchesFilters(s, { skipQuestionnaire: true })) {
      questionnaireCounts[''] += 1;
      const key = s.questionnaire_status?.overall || 'none';
      if (key in questionnaireCounts) questionnaireCounts[key] += 1;
    }
  }
  // Counts for OTIF band chips — apply all filters except the OTIF one.
  // Only approved (non-group) suppliers carry a delivery rate.
  const otifBandCounts: Record<string, number> = { '': 0 };
  for (const b of otifBands) otifBandCounts[b.grade] = 0;
  for (const s of suppliers) {
    if (matchesFilters(s, { skipOtif: true })) {
      if (s.is_supplier_group) continue;
      otifBandCounts[''] += 1;
      const key = otifBandOf(s);
      if (key && key in otifBandCounts) otifBandCounts[key] += 1;
    }
  }

  // Reset to page 1 when filters, the page size or the top-bar view change — a
  // page number from another list (or another page size) shows a different slice.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, currentTab, filters, viewKey, pageSize]);

  // Paginated subset of filtered suppliers
  const totalPages = Math.ceil(filteredSuppliers.length / pageSize);
  const paginatedSuppliers = filteredSuppliers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSearch = () => {
    setSearchQuery(searchRef.current?.value || '');
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Supplier code typeahead options derived from approved suppliers with a code set.
  const supplierCodeOptions = suppliers
    .filter((s) => !s.is_supplier_group)
    .map((s) => {
      const code = (s.company_details?.supplier_code || '').trim();
      if (!code) return null;
      return { value: code, label: `${code} — ${s.company_name}` };
    })
    .filter((o): o is { value: string; label: string } => o !== null)
    .sort((a, b) => a.value.localeCompare(b.value));

  return {
    // Navigation
    navigate,

    // Loading states
    loading,
    error,

    // Data
    suppliers,
    filteredSuppliers,
    stats,
    mapLocations,

    // Filters
    currentTab,
    setCurrentTab,
    selectedGroupName,
    setSelectedGroupName,
    showFilters,
    setShowFilters,
    filters,
    setFilters,
    riskCounts,
    questionnaireCounts,
    otifBandCounts,
    deliveryMetricMode,
    otifBands,
    siteOptions,
    commodityGroupOptions,
    commodityTypeOptions,
    supplierCodeOptions,

    // Search
    searchRef,
    searchQuery,
    handleSearch,
    handleSearchKeyDown,
    handleClearSearch,

    // Pagination
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    paginatedSuppliers,

    // Actions
    fetchData,
  };
}
